import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth } from '../auth/useAuth';
import { CourseSearchItem } from '../types';
import { profileApi } from '../api/profileApi';
import { useProfile } from './ProfileContext';
import { coursesStorageKey } from '../constants/storage';

interface DeviceCoursesState {
  /** Courses the user has saved to this device. */
  savedCourses: CourseSearchItem[];
  /** The course currently selected to play, referenced by its catalog id. */
  selectedCourseId: string | null;
  /** Current hole for the active round. Device-local, matching iOS and Android. */
  currentHole: number;
}

const DEFAULT_STATE: DeviceCoursesState = { savedCourses: [], selectedCourseId: null, currentHole: 1 };

interface DeviceCoursesContextValue extends DeviceCoursesState {
  saveCourse: (course: CourseSearchItem) => void;
  removeCourse: (id: string) => void;
  selectCourse: (id: string | null) => void;
  setCurrentHole: (hole: number) => void;
  /** The resolved selected course, or null when none is chosen. */
  selectedCourse: CourseSearchItem | null;
  /** True once the server-side profile pull has completed (or failed) for this user. */
  hydrated: boolean;
}

const DeviceCoursesContext = createContext<DeviceCoursesContextValue | undefined>(undefined);

/** Reads this golfer's cached saved-course list. Keyed per user so accounts on a shared device never mix. */
function loadState(userId: string): DeviceCoursesState {
  if (!userId) return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(coursesStorageKey(userId));
    if (raw) return { ...DEFAULT_STATE, ...JSON.parse(raw) };
  } catch {
    // ignore malformed/unavailable storage
  }
  return DEFAULT_STATE;
}

export const DeviceCoursesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { profile } = useProfile();
  const userId = profile.userId;
  const [state, setState] = useState<DeviceCoursesState>(() => loadState(userId));
  // Guards the server round-trip so hydration doesn't immediately re-persist.
  const hydratedRef = useRef(false);
  // Mirrors hydratedRef as render state so consumers can wait for the pull.
  const [hydrated, setHydrated] = useState(false);

  // When the signed-in golfer changes, immediately swap to that golfer's cached
  // courses so we never render — or persist — one user's list under another's id.
  useEffect(() => {
    hydratedRef.current = false;
    setHydrated(false);
    setState(loadState(userId));
  }, [userId]);

  // Cache to localStorage (namespaced by user) for a fast first paint / offline fallback.
  useEffect(() => {
    if (!userId) return;
    try {
      localStorage.setItem(coursesStorageKey(userId), JSON.stringify(state));
    } catch {
      // ignore quota / private-mode errors
    }
  }, [state, userId]);

  const persistCourses = (next: DeviceCoursesState) => {
    if (!userId) return;
    profileApi.saveCourses(userId, next.savedCourses, next.selectedCourseId).catch(() => {
      // Best-effort: the localStorage cache still holds the change.
    });
  };

  // Hydrate from the server-side profile on load so the saved courses and the
  // Play selection survive reloads and sync across devices. Falls back to the
  // cached localStorage copy when offline. The server is the source of truth, so a
  // user with no saved courses gets a clean list (never the previous account's).
  useEffect(() => {
    if (!isAuthenticated || !userId) return;
    let cancelled = false;
    (async () => {
      try {
        const remote = await profileApi.get(userId);
        if (cancelled) return;

        if (remote?.courses && remote.courses.length > 0) {
          // Server has data — it wins.
          setState({
            savedCourses: remote.courses,
            selectedCourseId: remote.selectedCourseId ?? null,
            currentHole: remote.currentHole ?? loadState(userId).currentHole,
          });
        } else if (remote) {
          // Server reachable but empty for this user. Migrate any of *this user's*
          // local-only courses up; otherwise start from a clean, empty list.
          const cached = loadState(userId);
          if (cached.savedCourses.length > 0) {
            persistCourses(cached);
            setState(cached);
          } else {
            setState(DEFAULT_STATE);
          }
        } else {
          // A missing server profile may have been deleted by staff. Discard this user's
          // device cache so its saved courses cannot be restored during later onboarding.
          localStorage.removeItem(coursesStorageKey(userId));
          setState(DEFAULT_STATE);
        }
      } catch {
        // Offline / API unavailable — keep this user's cached courses.
        if (!cancelled) setState(loadState(userId));
      } finally {
        if (!cancelled) {
          hydratedRef.current = true;
          setHydrated(true);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, userId]);

  const applyChange = (next: DeviceCoursesState) => {
    setState(next);
    if (hydratedRef.current) persistCourses(next);
  };

  const saveCourse = (course: CourseSearchItem) => {
    if (state.savedCourses.some((c) => c.id === course.id)) return;
    applyChange({ ...state, savedCourses: [...state.savedCourses, course] });
  };

  const removeCourse = (id: string) =>
    applyChange({
      savedCourses: state.savedCourses.filter((c) => c.id !== id),
      selectedCourseId: state.selectedCourseId === id ? null : state.selectedCourseId,
      currentHole: state.selectedCourseId === id ? 1 : state.currentHole,
    });

  const selectCourse = (id: string | null) => applyChange({
    ...state,
    selectedCourseId: id,
    currentHole: id !== state.selectedCourseId ? 1 : state.currentHole,
  });

  const setCurrentHole = (hole: number) => {
    const clamped = Math.max(1, Math.round(hole));
    setState((current) => ({ ...current, currentHole: clamped }));
    if (userId && hydratedRef.current) {
      profileApi.saveRound(userId, state.selectedCourseId, clamped).catch(() => {
        // Best-effort: the localStorage cache still holds the change.
      });
    }
  };

  const selectedCourse =
    state.savedCourses.find((c) => c.id === state.selectedCourseId) ?? null;

  return (
    <DeviceCoursesContext.Provider
      value={{ ...state, saveCourse, removeCourse, selectCourse, setCurrentHole, selectedCourse, hydrated }}
    >
      {children}
    </DeviceCoursesContext.Provider>
  );
};

export const useDeviceCourses = (): DeviceCoursesContextValue => {
  const ctx = useContext(DeviceCoursesContext);
  if (!ctx) throw new Error('useDeviceCourses must be used within a DeviceCoursesProvider');
  return ctx;
};
